/**
 * @file Shared HTTP route contracts for `@labcraft/www`.
 * @module @labcraft/www/shared/routes
 *
 * Route contracts define the wire protocol between client and server:
 * method, path, request schema, response schema. Handlers bind to
 * these contracts via `BackendService.bindRoute()`; the SPA-side
 * frontend `Www` consumes the same contracts through `HttpClient`.
 *
 * Paths here are wire-true — each route's `.path` is the full URL
 * the daemon serves it at. The contact endpoint is verb-paired:
 * `GET /api/contact` mints a CAPTCHA challenge, `POST /api/contact`
 * submits the form (carrying the solved challenge in the body).
 * `bindRoute` stores the path as-authored; the framework never
 * manipulates it.
 */

import { z } from 'zod'
import { defineRoute } from '@labcraft/core'

////////////////////////////////////////////////////////////////////////////////
// CAPTCHA (proof-of-work challenge / solution)
////////////////////////////////////////////////////////////////////////////////

/**
 * Server-minted challenge issued at `GET /api/contact`. Stateless:
 * the server doesn't remember issued challenges — the HMAC `sig` over
 * `(nonce, difficulty, expiresAt)` keyed with the
 * `contact.captchaSecret` server secret is what authenticates a
 * solution at verify time.
 *
 * @property nonce - 16-byte random value, base64url-encoded.
 * @property difficulty - Target leading zero bits in
 *   `sha256(nonce || ':' || counter)`. 20 in production (~1M attempts;
 *   ~1 second on a modern browser). Tests use lower values for speed.
 * @property expiresAt - Unix-ms expiry; the server rejects solutions
 *   submitted after this timestamp.
 * @property sig - HMAC-SHA-256 over `${nonce}:${difficulty}:${expiresAt}`
 *   keyed with the server secret. Base64url-encoded.
 */
export const ChallengeResponseSchema = z.object({
  nonce: z.string().min(1),
  difficulty: z.number().int().positive(),
  expiresAt: z.number().int().positive(),
  sig: z.string().min(1),
})

/** Parsed `ChallengeResponse`. */
export type ChallengeResponse = z.infer<typeof ChallengeResponseSchema>

/**
 * `GET /api/contact` — mint a fresh PoW challenge. Stateless (no Redis
 * entry); the HMAC sig is what the server checks on verify at
 * `POST /api/contact`. The CAPTCHA exists only to protect the contact
 * form, so it lives on the contact resource rather than a standalone
 * `/api/captcha/*` namespace.
 */
export const ChallengeRoute = defineRoute({
  method: 'GET',
  path: '/api/contact',
  response: ChallengeResponseSchema,
  description:
    'Mint a fresh PoW challenge (nonce + difficulty + expiry + HMAC sig).',
})

/**
 * Client-submitted solution to a PoW challenge. Returns the original
 * challenge fields verbatim so the server can re-check the HMAC, plus
 * the discovered `counter` that makes
 * `sha256(nonce || ':' || counter)` have ≥ `difficulty` leading zero
 * bits.
 *
 * @property nonce - Echoed from the challenge.
 * @property difficulty - Echoed from the challenge.
 * @property expiresAt - Echoed from the challenge.
 * @property sig - Echoed from the challenge; server re-derives + checks.
 * @property counter - Non-negative integer the client found by
 *   brute-force SHA-256.
 */
export const CaptchaSolutionSchema = z.object({
  nonce: z.string().min(1),
  difficulty: z.number().int().positive(),
  expiresAt: z.number().int().positive(),
  sig: z.string().min(1),
  counter: z.number().int().nonnegative(),
})

/** Parsed `CaptchaSolution`. */
export type CaptchaSolution = z.infer<typeof CaptchaSolutionSchema>

////////////////////////////////////////////////////////////////////////////////
// Contact
////////////////////////////////////////////////////////////////////////////////

/** Subject character ceiling — fits comfortably in an inbox row. */
export const CONTACT_SUBJECT_MAX = 150

/**
 * Body accepted by `POST /api/contact`. Mirrors the form on the
 * contact page; client-side validation is advisory and authoritative
 * validation runs here.
 *
 * @property name    - Sender's name; non-empty.
 * @property email   - Sender's email; standard format.
 * @property subject - Short topic line from the sender; non-empty,
 *   bounded by `CONTACT_SUBJECT_MAX`. The outbound email's `Subject:`
 *   is the template-owned prefix `[www/contact]` plus this string —
 *   the prefix lives in the email template, not in this user-supplied
 *   field.
 * @property message - Body of the message; up to 4 000 chars.
 * @property captcha - PoW solution from `<CaptchaChallenge>`. The
 *   server re-derives the HMAC + checks the leading-zero-bits + checks
 *   the expiry; missing or invalid → 400.
 */
export const ContactRequestSchema = z.object({
  name: z.string().min(1, 'name is required'),
  email: z.string().email('email looks invalid'),
  subject: z
    .string()
    .min(1, 'subject is required')
    .max(
      CONTACT_SUBJECT_MAX,
      `subject exceeds ${CONTACT_SUBJECT_MAX} character limit`,
    ),
  message: z
    .string()
    .min(1, 'message is required')
    .max(4000, 'message exceeds 4 000 character limit'),
  captcha: CaptchaSolutionSchema,
})

/** Parsed `POST /api/contact` body. */
export type ContactRequest = z.infer<typeof ContactRequestSchema>

/** Empty success body returned by `POST /api/contact`. */
export const ContactResponseSchema = z.object({})

/** Parsed `POST /api/contact` response. */
export type ContactResponse = z.infer<typeof ContactResponseSchema>

/**
 * `POST /api/contact` — accept a contact-form submission. Success is
 * indicated by HTTP 200 + an empty JSON body; the daemon forwards the
 * message to the operator inbox asynchronously.
 */
export const ContactRoute = defineRoute({
  method: 'POST',
  path: '/api/contact',
  request: ContactRequestSchema,
  response: ContactResponseSchema,
  description: 'Accept a contact-form submission.',
})

////////////////////////////////////////////////////////////////////////////////
// Runtime config (SPA bootstrap)
////////////////////////////////////////////////////////////////////////////////

/**
 * Runtime config the islands fetch once per page load. Carries the two
 * values the static pages can't bake at build time. (Analytics is NOT
 * here — the tracker is baked into the documents at build per ADR-026;
 * see `WwwDocument`.)
 *
 * @property waitlistEnabled - Mirror of the operator-controlled
 *   account setting `waitlist_learners`, read server-side from the
 *   shared `system_settings` table. **Display-only** — the marketing
 *   copy switches on it (waitlist vs. open), but the access decision is
 *   enforced independently inside the account daemon's signin callback,
 *   never from this value. A tampered client copy changes only which
 *   copy renders, not who gets activated.
 * @property labOrigin - Browser-facing base URL of lab. Served from the
 *   daemon's own `auth.labOrigin` config so lab-bound navigation points
 *   at the right host in dev / harness / prod without a build-time
 *   `VITE_LAB_ORIGIN` (closes #794).
 */
export const WwwConfigResponseSchema = z.object({
  waitlistEnabled: z.boolean(),
  labOrigin: z.string().url(),
})

/** Parsed `WwwConfigResponse`. */
export type WwwConfigResponse = z.infer<typeof WwwConfigResponseSchema>

/**
 * `GET /api/config` — runtime config for the static pages' islands.
 * Read fresh per request (the account setting is operator-toggleable
 * at runtime; the daemon's Settings cache is kept live by the
 * `lc_settings` `pg_notify` trigger), so an admin flip reflects on the
 * next page load with no rebuild and no restart.
 */
export const ConfigRoute = defineRoute({
  method: 'GET',
  path: '/api/config',
  response: WwwConfigResponseSchema,
  description: 'Runtime config (waitlist mode + lab origin) for the islands.',
})

////////////////////////////////////////////////////////////////////////////////
// Unsubscribe (RFC 8058 one-click, public front for the email daemon; #1598)
////////////////////////////////////////////////////////////////////////////////

/**
 * Query params on the public unsubscribe endpoints. The `token` is the
 * signed per-recipient value the email daemon minted into the marketing
 * message's `List-Unsubscribe` URL; www forwards it to the loopback
 * email daemon, which verifies + records the suppression.
 *
 * @property token - Signed unsubscribe token (opaque to www).
 */
export const UnsubscribeQuerySchema = z.object({
  token: z.string().min(1),
})

/** Parsed unsubscribe query params. */
export type UnsubscribeQuery = z.infer<typeof UnsubscribeQuerySchema>

/**
 * Nominal response schema for both unsubscribe routes. The GET handler
 * returns a small branded HTML confirmation page and the POST handler an
 * empty JSON body; no typed client consumes either (browsers + mail
 * clients do), so the schema is a placeholder the framework never
 * enforces server-side.
 */
export const UnsubscribeResponseSchema = z.object({})

/** Parsed unsubscribe response. */
export type UnsubscribeResponse = z.infer<typeof UnsubscribeResponseSchema>

/**
 * `GET /api/unsubscribe?token=…` — the visible "Unsubscribe" link in a
 * marketing email. www forwards the token to the email daemon and
 * renders a confirmation page. A missing/invalid token renders an
 * "invalid link" page (still HTTP 200-shaped for the browser).
 */
export const UnsubscribeGetRoute = defineRoute({
  method: 'GET',
  path: '/api/unsubscribe',
  query: UnsubscribeQuerySchema,
  response: UnsubscribeResponseSchema,
  description: 'Public unsubscribe landing (forwards to the email daemon).',
})

/**
 * `POST /api/unsubscribe?token=…` — the RFC 8058 one-click target that
 * mail clients POST (`List-Unsubscribe-Post: List-Unsubscribe=One-Click`).
 * The token rides the query; the body (`List-Unsubscribe=One-Click`) is
 * ignored. www forwards the token to the email daemon and returns 200.
 */
export const UnsubscribePostRoute = defineRoute({
  method: 'POST',
  path: '/api/unsubscribe',
  query: UnsubscribeQuerySchema,
  response: UnsubscribeResponseSchema,
  description: 'Public one-click unsubscribe (forwards to the email daemon).',
})

////////////////////////////////////////////////////////////////////////////////
// Resend delivery webhook (public front for the email daemon; #1598 Part 5)
////////////////////////////////////////////////////////////////////////////////

/**
 * Nominal response for the public webhook proxy. www mirrors the email
 * daemon's status back to Resend; the body is opaque to Resend.
 */
export const ResendWebhookResponseSchema = z.object({ ok: z.boolean() })

/** Parsed webhook proxy response. */
export type ResendWebhookResponse = z.infer<typeof ResendWebhookResponseSchema>

/**
 * `POST /api/resend/webhook` — the public Resend event receiver. www
 * forwards the RAW body + `svix-*` headers verbatim to the loopback email
 * daemon (which verifies the signature against those exact bytes before
 * parsing) and mirrors the daemon's status back so Resend retries on a
 * non-2xx. No `request` schema — the body is proxied untouched.
 */
export const ResendWebhookRoute = defineRoute({
  method: 'POST',
  path: '/api/resend/webhook',
  response: ResendWebhookResponseSchema,
  description: 'Public Resend webhook receiver (raw-forwards to email daemon).',
})
